import { Group, Stack } from "@mantine/core"
import { useClipboard, useInterval, useMediaQuery } from "@mantine/hooks"
import { useQuery } from "@tanstack/react-query"
import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { AppLink, AppTitle, CityTag, DateTime, icons, RoleTag, TagBox, UserTeams } from "@/components/utils"
import { useAuth } from "@/hooks"
import { theme } from "@/styles"
import type { TUser } from "@/types"
import { anniversaryDiff, dayjs, hasAdmin, hasUserManager, peopleURL, userAPI } from "@/utils"
import { Impersonate } from "@/utils/impersonate"
import { UserAvatar } from "../UserAvatar"
import { UserEdit } from "../UserEdit"
import { EditButton, Panel } from "./utils"

export function ProfileInformation({ user }: { user: TUser }) {
  const { t } = useTranslation()
  const sm = useMediaQuery(`(max-width: ${(theme.breakpoints as any).sm})`)

  const { data: { user_info } = {} } = useQuery({
    queryKey: ["details", user.id],
    queryFn: () => userAPI.details(user.id),
  })

  const { user: curUser } = useAuth()
  const admin = hasAdmin(curUser)
  const editable = hasUserManager(curUser as TUser, user)
  const isSelf = curUser?.id === user.id

  return (
    <Panel block sm={{ gridColumnEnd: "span 2" }}>
      <Group align="start" gap={sm ? "lg" : undefined} wrap={sm ? "wrap" : "nowrap"}>
        <AppLink href={user.picture} target="_blank" rel="noopener noreferrer">
          <UserAvatar user={user} size={220} variant="gradient" style={{ flexGrow: 0 }} radius="lg" />
        </AppLink>
        <Stack ml={sm ? 0 : "lg"} gap="sm" style={{ flexGrow: 1, minWidth: sm ? 260 : undefined }}>
          <Group justify="space-between">
            <AppTitle mr="xl">{user.position ? user.position : t("Information")}</AppTitle>
            {(editable || isSelf) && user.is_active && (
              <EditButton title={t("Information")} modalProps={{ modalId: UserEdit.modalId(user.id) }}>
                {editable ? (
                  <UserEdit userId={user.id} allowDelete={admin} />
                ) : (
                  <UserEdit userId={user.id} only={["city_id", "locale", "birthday"]} />
                )}
              </EditButton>
            )}
          </Group>

          <Group align="flex-start" gap="xs" wrap="wrap">
            <UserNotActive is_active={user.is_active} />

            <UserTeams user={user} />

            <UserCity city={user.city} />

            <UserEmail email={user.email} />

            <UserPhone phone={user_info?.phone} />

            {(editable || admin) && <RoleTag role={user.role} />}

            <UserSlack source={user.source} source_id={user.source_id} />

            <UserOrgChart id={user.id} is_active={user.is_active} />

            <Impersonate id={user.id} name={user.full_name || user.name}>
              <TagBox leftSection={<icons.Impersonate size={16} />} active>
                {t("Impersonate")}
              </TagBox>
            </Impersonate>
            <UserTime {...user} />
            <TagBox tooltip={t("Employee Locale")} leftSection={<icons.Language size={16} />}>
              {user.locale?.toUpperCase()}
            </TagBox>

            <UserBirthday birthday={user.birthday} />
            <UserDTStart dtstart={user.dtstart} />
          </Group>
        </Stack>
      </Group>
    </Panel>
  )
}

function UserEmail({ email }: Pick<TUser, "email">) {
  const { t } = useTranslation()
  const clipboard = useClipboard({ timeout: 500 })
  const copyText = (clipboard.copied ? t("Copied") : t("Copy")).toLowerCase()

  if (!email) return null

  return (
    <TagBox
      active
      onClick={() => clipboard.copy(email)}
      tooltip={`${t("Email")} (${copyText})`}
      leftSection={<icons.Email size={16} />}
    >
      {email}
    </TagBox>
  )
}

function UserOrgChart({ id, is_active }: Pick<TUser, "id" | "is_active">) {
  const { t } = useTranslation()
  if (!is_active) return null

  return (
    <TagBox leftSection={<icons.OrgChart size={16} />} href={`${peopleURL}?tab=orgchart&user=${id}`}>
      {t("Org Chart")}
    </TagBox>
  )
}

function UserBirthday({ birthday }: Pick<TUser, "birthday">) {
  const { t } = useTranslation()
  if (!birthday) return null

  const birthdayDays = anniversaryDiff(birthday)
  const birthdaySoon = birthdayDays && birthdayDays < 5

  return (
    <TagBox tooltip={t("Birthday")} leftSection={<icons.Birthday size={16} />} color={birthdaySoon ? "red" : undefined}>
      <DateTime timestamp={birthday} format="D MMM" />
    </TagBox>
  )
}

function UserDTStart({ dtstart }: Pick<TUser, "dtstart">) {
  const { t } = useTranslation()
  if (!dtstart) return null

  const anniversaryDays = anniversaryDiff(dtstart, 1)
  const anniversarySoon = anniversaryDays && anniversaryDays < 5

  return (
    <TagBox
      tooltip={t("Work Start")}
      leftSection={<icons.Company size={16} />}
      color={anniversarySoon ? "red" : undefined}
    >
      <DateTime timestamp={dtstart} format="D MMM YYYY" />
      {` (${dayjs(dtstart).fromNow(true)})`}
    </TagBox>
  )
}

function UserCity({ city }: Pick<TUser, "city">) {
  if (!city) return null
  return <CityTag city={city} />
}

function UserPhone({ phone }: { phone?: string | null }) {
  const { t } = useTranslation()
  const clipboard = useClipboard({ timeout: 500 })
  const copyText = (clipboard.copied ? t("Copied") : t("Copy")).toLowerCase()

  if (!phone) return null

  return (
    <TagBox
      active
      onClick={() => clipboard.copy(phone)}
      leftSection={<icons.Phone size={16} />}
      tooltip={`${t("Phone")} (${copyText})`}
    >
      {phone}
    </TagBox>
  )
}

function UserTime(user: TUser) {
  const { t } = useTranslation()

  // Update user time every second
  const [time, setTime] = useState(userTime(user))
  const interval = useInterval(() => setTime(userTime(user)), 1e3)
  useEffect(() => {
    setTime(userTime(user))
    interval.start()
    return () => interval.stop()
  }, [user])

  return (
    <TagBox tooltip={t("Local Employee Time")} leftSection={<icons.Time size={16} />}>
      {time}
    </TagBox>
  )
}

function userTime(user: TUser) {
  const tz = user.tz || "UTC"
  return dayjs().tz(tz).format("HH:mm")
}

function UserNotActive({ is_active }: Pick<TUser, "is_active">) {
  const { t } = useTranslation()
  if (is_active) return null

  return (
    <TagBox
      fullWidth
      color="red"
      variant="filled"
      tooltip={t("Account Inactive")}
      leftSection={<icons.Fail size={16} />}
    >
      {t("Deactivated")}
    </TagBox>
  )
}

function UserSlack({ source, source_id }: Pick<TUser, "source" | "source_id">) {
  const { company } = useAuth()
  if (source !== "slack" || !source_id) return null

  return (
    <TagBox leftSection={<icons.Slack size={16} />} href={`slack://user?team=${company?.source_id}&id=${source_id}`}>
      Slack
    </TagBox>
  )
}
